const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "loading chunk",
  "chunkloaderror",
]

const DEFAULT_CHUNK_ERROR_SIGNATURE = "unknown-chunk-load-error"

/**
 * Builds a normalized signature for a chunk-load style error.
 *
 * @param error - Unknown error value captured by route error boundaries
 * @returns A stable signature string to distinguish unique chunk load failures
 */
export const getChunkLoadErrorSignature = (error: unknown): string => {
  let message = ""

  if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") {
    message = error.message.toLowerCase()
  } else if (typeof error === "string") {
    message = error.toLowerCase()
  }

  const trimmed = message.trim()

  if (trimmed.length === 0) {
    return DEFAULT_CHUNK_ERROR_SIGNATURE
  }

  return trimmed
}

/**
 * Returns true when an error likely indicates that the browser is trying to load
 * a stale hashed bundle chunk after a deploy or after restoring an unloaded tab.
 *
 * @param error - Unknown error value captured by route error boundaries
 * @returns True when a full-page reload is a safe recovery strategy
 */
export const isRecoverableChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false
  }

  const name =
    typeof error === "object" && error !== null && "name" in error && typeof error.name === "string"
      ? error.name.toLowerCase()
      : ""

  const message = getChunkLoadErrorSignature(error)

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => {
    return name.includes(pattern) || message.includes(pattern)
  })
}
